import { create } from 'zustand';
import { useEffect } from 'react';
import { requestGetPromotionServerTime } from '@apis/renewal/promotion';

interface ServerTimeState {
  // 상태
  serverTime: string | null;
  serverTimeOffset: number | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;

  // 액션
  fetchServerTime: () => Promise<void>;
  getAdjustedTime: () => Date;
  isCurrentDateInRange: (startDate: string, endDate: string) => boolean;
  clearError: () => void;

  // 자동 갱신 관련
  autoRefresh: boolean;
  refreshInterval: number;
  setAutoRefresh: (enabled: boolean) => void;
  setRefreshInterval: (interval: number) => void;
}

export const useServerTimeStore = create<ServerTimeState>((set, get) => ({
  // 초기 상태
  serverTime: null,
  serverTimeOffset: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  autoRefresh: true,
  refreshInterval: 60000, // 1분

  // 서버타임 조회
  fetchServerTime: async () => {
    const state = get();
    if (state.isLoading) return; // 이미 로딩 중이면 중복 요청 방지

    set({ isLoading: true, error: null });

    try {
      const response = await requestGetPromotionServerTime();
      const serverTimeString = response;
      const serverTimeMs = new Date(serverTimeString).getTime();
      const localTimeMs = Date.now();
      const offset = serverTimeMs - localTimeMs;

      set({
        serverTime: serverTimeString,
        serverTimeOffset: offset,
        isLoading: false,
        lastUpdated: Date.now(),
      });
    } catch (error) {
      console.error('서버타임 조회 실패:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : '서버타임 조회 실패',
        serverTimeOffset: 0, // 실패 시 로컬 시간 사용
      });
    }
  },

  // 조정된 시간 반환
  getAdjustedTime: () => {
    const { serverTimeOffset } = get();
    return new Date(Date.now() + (serverTimeOffset ?? 0));
  },

  // 날짜 범위 체크
  isCurrentDateInRange: (startDate: string, endDate: string) => {
    const { serverTimeOffset, getAdjustedTime } = get();
    // if (serverTimeOffset === null) return false;
    const now = getAdjustedTime();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return now >= start && now < end;
  },

  // 에러 클리어
  clearError: () => set({ error: null }),

  // 자동 갱신 설정
  setAutoRefresh: (enabled: boolean) => set({ autoRefresh: enabled }),
  setRefreshInterval: (interval: number) => set({ refreshInterval: interval }),
}));

// 서버사이드 시간으로 즉시 초기화하는 유틸리티 함수
export const initializeServerTime = (serverTime: string) => {
  let serverTimeMs: number;

  // 시간 정보가 없는 날짜 형식(YYYY-MM-DD)인 경우 한국 시간대 기준 11:00:00으로 설정
  if (/^\d{4}-\d{2}-\d{2}$/.test(serverTime)) {
    serverTimeMs = new Date(`${serverTime}T11:00:00+09:00`).getTime();
  } else {
    serverTimeMs = new Date(serverTime).getTime();
  }

  const localTimeMs = Date.now();
  const offset = serverTimeMs - localTimeMs;

  useServerTimeStore.setState({
    serverTime,
    serverTimeOffset: offset,
    isLoading: false,
    lastUpdated: Date.now(),
    error: null,
  });
};

// 편의를 위한 셀렉터 훅들
export const useServerTime = () =>
  useServerTimeStore(state => ({
    serverTime: state.serverTime,
    serverTimeOffset: state.serverTimeOffset,
    isLoading: state.isLoading,
    error: state.error,
    lastUpdated: state.lastUpdated,
  }));

export const useServerTimeActions = (options?: {
  initialServerTime?: string;
}) => {
  const actions = useServerTimeStore(state => ({
    fetchServerTime: state.fetchServerTime,
    getAdjustedTime: state.getAdjustedTime,
    isCurrentDateInRange: state.isCurrentDateInRange,
    clearError: state.clearError,
    setAutoRefresh: state.setAutoRefresh,
    setRefreshInterval: state.setRefreshInterval,
  }));

  const isInitialized = useServerTimeStore(
    state => state.serverTimeOffset !== null
  );

  // useEffect 대신 바로 초기화
  if (options?.initialServerTime && !isInitialized) {
    initializeServerTime(options.initialServerTime);
  }

  return actions;
};

export const useServerTimeOffset = () =>
  useServerTimeStore(state => state.serverTimeOffset);
export const useAdjustedTime = () =>
  useServerTimeStore(state => state.getAdjustedTime());
export const useIsDateInRange = (startDate: string, endDate: string) =>
  useServerTimeStore(state => state.isCurrentDateInRange(startDate, endDate));

// 자동 갱신을 위한 훅
export const useServerTimeAutoRefresh = () => {
  const { autoRefresh, refreshInterval, lastUpdated, fetchServerTime } =
    useServerTimeStore();

  // 자동 갱신 로직은 컴포넌트에서 useEffect로 처리
  return {
    autoRefresh,
    refreshInterval,
    lastUpdated,
    fetchServerTime,
  };
};
